import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { database } from "../firebaseConfig";
import { ref, get } from "firebase/database";
import { useNavigate } from "react-router-dom";
import ProductCard from "../components/ProductCard"; // Your generic ProductCard component
import "./About.css";
import { config } from '../config/config'
const API_URL = config.baseUrl;
const AboutPage = () => {
    const [config, setConfig] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [selectedCertificate, setSelectedCertificate] = useState(null);
    const navigate = useNavigate();

    // Update mobile flag on resize
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Fetch configuration data from Firebase
    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const snapshot = await get(ref(database, "aboutusconfig"));
                if (snapshot.exists()) {
                    setConfig(snapshot.val());
                } else {
                    console.error("No About Us configuration found in Firebase.");
                }
            } catch (error) {
                console.error("Error fetching About Us configuration:", error);
            }
        };
        fetchConfig();
    }, []);

    if (!config) {
        return (
            <div className="loading-container" style={{ width: "100%" }}>
                <img
                    src={`${API_URL}uploads/loading.gif`}
                    alt="Loading..."
                    className="loading-gif"
                    style={{ width: "100%" }}
                />
            </div>
        );
    }

    // Destructure the configuration.
    const { general, hero, attraction, certificates, seo } = config;
    const bodyBackgroundColor = general?.bodyBackgroundColor || "#ffffff";

    // --------------------
    // 1) HERO SECTION
    // --------------------
    const heroImageUrl = isMobile && hero.mobileImageUrl ? hero.mobileImageUrl : hero.desktopImageUrl;
    const heroSection = (
        <div
            className="Aboutus-container"
            style={{ backgroundColor: hero.backgroundColor || "#ffffff" }}
        >
            {heroImageUrl && (
                <div className="Aboutus-image-container">
                    <img src={heroImageUrl} alt="Hero Background" />
                </div>
            )}
            <div className="Aboutus-text-overlay">
                <h1 className="Aboutus-main-heading">
                    {hero.heading ? hero.heading : "About Us"}
                </h1>
                <p className="Aboutus-description-text">
                    {hero.text ||
                        "We are committed to providing trusted products and exceptional service."}
                </p>
            </div>
        </div>
    );

    // --------------------
    // 2) CERTIFICATE SECTION
    // --------------------
    const renderCertificateSection = (cert, keySuffix, isMobile) => {
        const flexDir = cert.flexDirection;
        const containerStyle = {
            display: "flex",
            ...(flexDir !== "row-reverse" && { flexDirection: flexDir }),
            alignItems: "center",
            padding: "20px",
            backgroundColor: cert.backgroundColor || "#ffffff",
            justifyContent: "space-around",
        };

        const imageOrder = flexDir === "row" ? 1 : 2;
        const textOrder = flexDir === "row" ? 2 : 1;

        return (
            <div key={`certificate-${keySuffix}`} style={containerStyle}>
                {cert.imageUrl && (
                    <img
                        src={cert.imageUrl}
                        alt="Certificate"
                        style={{
                            width: "25%",
                            objectFit: "cover",
                            borderRadius: "8px",
                            order: imageOrder,
                            cursor: isMobile ? "pointer" : "default",
                        }}
                        onClick={() => {
                            if (isMobile) {
                                setSelectedCertificate(cert);
                            }
                        }}
                    />
                )}
                <p
                    style={{
                        fontSize: isMobile ? "18px" : "35px",
                        color: "#333",
                        maxWidth: "400px",
                        order: textOrder,
                        ...(textOrder === 2 && { paddingLeft: "10%" }), // ✅ only apply paddingLeft if textOrder is 1
                    }}
                >
                    {cert.description}
                </p>
            </div>
        );
    };


    // --------------------
    // 3) ATTRACTION SECTION
    // --------------------
    const attractionSection = (
        <div
            className="Aboutus-categories-section"
            style={{ backgroundColor: "#ffffff", padding: "20px" }}
        >
            <div
                className="categories-list"
                style={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${window.innerWidth < 768 ? 2 : 3}, 1fr)`,
                    gap: "20px",
                }}
            >
                {attraction &&
                    attraction.images &&
                    attraction.images.map((item, index) => (
                        <ProductCard
                            key={item.id || index}
                            title=""
                            description=""
                            mediaUrl={item.imageUrl || "https://via.placeholder.com/300x200"}
                            mediaType="image"
                            edit={false}
                            cardsPerRow={window.innerWidth < 768 ? 2 : 3}
                            cardsPerRowPhoneView={2}
                            onClick={() => navigate(`/OurProducts`)}
                        />
                    ))}
            </div>
        </div>
    );

    // --------------------
    // 4) FINAL CALL-TO-ACTION SECTION (NEW)
    // --------------------
    const finalSection = (
        <div
            style={{
                backgroundColor: "#fdeaff",
                padding: "20px",
                borderRadius: "8px",
                textAlign: "center",
                maxWidth: "90%",
                margin: "0 auto",
            }}
        >
            <h2 style={{ color: "#8a2be2", marginBottom: "10px" }}>
                Switch to Mariott Pharmaceuticals and feel the change!
            </h2>
            <button
                onClick={() => navigate("/OurProducts")}
                style={{
                    backgroundColor: "#8a2be2",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    padding: "10px 20px",
                    cursor: "pointer",
                    fontSize: "16px",
                }}
            >
                Buy Now
                <span style={{ marginLeft: "5px" }} role="img" aria-label="cart">
                    🛒
                </span>
            </button>
        </div>
    );

    // --------------------
    // 5) BUILD SECTIONS ARRAY
    // --------------------
    const sections = [];

    sections.push({
        key: "hero",
        order: hero.order !== undefined ? hero.order : 0,
        content: heroSection,
    });

    if (certificates && certificates.length > 0) {
        certificates.forEach((cert) => {
            sections.push({
                key: `certificate-${cert.id}`,
                order: cert.order !== undefined ? cert.order : 1,
                content: renderCertificateSection(cert, cert.id, isMobile),
            });
        });
    }

    sections.push({
        key: "attraction",
        order: attraction.order !== undefined ? attraction.order : 2,
        content: attractionSection,
    });

    sections.push({
        key: "finalSection",
        order: 999,
        content: finalSection,
    });

    sections.sort((a, b) => a.order - b.order);

    return (
        <div style={{ backgroundColor: bodyBackgroundColor }}>
            {/* Dynamically set meta data using React Helmet */}
            <Helmet>
                <title>About Us - Marriott Pharmaceuticals</title>
                <meta name="description" content={seo?.metaDescription || "Learn more about us."} />
                <meta name="keywords" content={seo?.metaKeywords || "About Us, Company, Excellence"} />
            </Helmet>

            {sections.map((section) => (
                <div key={section.key} style={{ padding: "20px" }}>
                    {section.content}
                </div>
            ))}

            {/* Modal for enlarged certificate in phone view */}
            {selectedCertificate && (
                <div
                    className="modal-overlay"
                    onClick={() => setSelectedCertificate(null)}
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 1000,
                    }}
                >
                    <div
                        className="modal-content"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            backgroundColor: "#fff",
                            padding: "20px",
                            borderRadius: "8px",
                            maxWidth: "90%",
                            maxHeight: "90%",
                            overflow: "auto",
                        }}
                    >
                        <img
                            src={selectedCertificate.imageUrl}
                            alt="Enlarged Certificate"
                            style={{ width: "100%", height: "auto", borderRadius: "8px" }}
                        />
                        <p style={{ marginTop: "10px", fontSize: "16px", color: "#333" }}>
                            {selectedCertificate.description}
                        </p>
                        <button
                            onClick={() => setSelectedCertificate(null)}
                            style={{
                                marginTop: "10px",
                                padding: "10px 20px",
                                fontSize: "16px",
                                borderRadius: "4px",
                                border: "none",
                                backgroundColor: "#333",
                                color: "#fff",
                                cursor: "pointer",
                            }}
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AboutPage;
